import type { CalendarEvent, CalendarApiResponse } from '../types';
import { getMessagingApiUrl } from '@/lib/platformUtils';

// NOTE: URL is now obtained dynamically via getMessagingApiUrl() at runtime

// Team settings for calendar visibility
export interface TeamCalendarSettings {
  calendar_visibility?: 'full_details' | 'busy_only' | 'available_only' | 'hidden';
  show_event_titles?: boolean;
  show_event_locations?: boolean;
  show_event_attendees?: boolean;
  auto_add_members?: boolean;
  allow_member_invite?: boolean;
  require_approval?: boolean;
  notify_on_member_join?: boolean;
  notify_on_member_leave?: boolean;
  notify_on_event_create?: boolean;
  default_working_hours?: {
    start: string;
    end: string;
    timezone?: string;
  };
}

// Team interface for calendar team selection
export interface CalendarTeam {
  id: string;
  team_name: string;
  team_key: string;
  color: string;
  icon: string;
  description?: string;
  settings?: TeamCalendarSettings;
}

class CalendarApiService {
  private getAuthHeaders(): Record<string, string> {
    let token: string | null = null;
    
    if (typeof window !== 'undefined') {
      // Try to get token from auth_tokens (standard auth storage)
      const storedTokens = localStorage.getItem('auth_tokens');
      if (storedTokens) {
        try {
          const parsed = JSON.parse(storedTokens);
          token = parsed.access_token || null;
        } catch (e) {
          console.error('[CalendarAPI] Failed to parse auth_tokens:', e);
        }
      }
    }
    
    return token
      ? {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }
      : {
          'Content-Type': 'application/json',
        };
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<CalendarApiResponse<T>> {
    try {
      const response = await fetch(`${getMessagingApiUrl()}${endpoint}`, {
        ...options,
        headers: {
          ...this.getAuthHeaders(),
          ...options.headers,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || data.message || `Request failed: ${response.status}`,
        };
      }

      // API returns { success: true, data: ... } - extract the nested data
      return { success: true, data: data.data !== undefined ? data.data : data };
    } catch (error: any) {
      console.error('Calendar API error:', error);
      return {
        success: false,
        error: error.message || 'Network error',
      };
    }
  }

  // Team Operations
  async getTeams(): Promise<CalendarApiResponse<CalendarTeam[]>> {
    return this.request<CalendarTeam[]>('/api/calendar/teams');
  }

  // Event Operations
  async getEvents(
    start?: Date | string,
    end?: Date | string,
    options?: { teamId?: string; includeTeamEvents?: boolean }
  ): Promise<CalendarApiResponse<CalendarEvent[]>> {
    const params = new URLSearchParams();
    if (start) {
      params.set('start', typeof start === 'string' ? start : start.toISOString());
    }
    if (end) {
      params.set('end', typeof end === 'string' ? end : end.toISOString());
    }
    if (options?.teamId) {
      params.set('team_id', options.teamId);
    }
    if (options?.includeTeamEvents) {
      params.set('include_team_events', 'true');
    }

    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await this.request<CalendarEvent[]>(`/api/calendar/events${query}`);
    // user_calendar_events.id is BIGINT — the backend returns numbers but the
    // UI treats ids as strings everywhere (split/encodeURIComponent/compare).
    if (res.data) {
      res.data = res.data.map((e) => ({ ...e, id: String(e.id) }));
    }
    return res;
  }

  async getEvent(eventId: string): Promise<CalendarApiResponse<CalendarEvent>> {
    return this.request<CalendarEvent>(`/api/calendar/events/${encodeURIComponent(eventId)}`);
  }

  async createEvent(
    event: Omit<CalendarEvent, 'id'> & { event_type?: string; isVideoMeeting?: boolean; team_id?: string; is_team_event?: boolean }
  ): Promise<CalendarApiResponse<CalendarEvent>> {
    // Determine event type from isVideoMeeting flag or explicit event_type
    const eventType = event.event_type || (event.isVideoMeeting ? 'online' : 'in-person');
    
    // Map frontend field names to backend field names
    const backendPayload = {
      title: event.title,
      description: event.description,
      location: event.location,
      start_time: event.start instanceof Date ? event.start.toISOString() : event.start,
      end_time: event.end instanceof Date ? event.end.toISOString() : event.end,
      all_day: event.allDay,
      color: event.color,
      calendar_name: event.calendarId,
      attendees: event.attendees,
      reminders: event.reminders || [{ minutes: 15 }],
      recurrence_rule: event.recurrence,
      event_type: eventType,
      team_id: event.team_id,
      is_team_event: event.is_team_event,
      // Don't send meeting_url - let the backend auto-generate it for online events
    };
    
    return this.request<CalendarEvent>('/api/calendar/events', {
      method: 'POST',
      body: JSON.stringify(backendPayload),
    });
  }

  async updateEvent(
    eventId: string,
    updates: Partial<CalendarEvent>
  ): Promise<CalendarApiResponse<CalendarEvent>> {
    // Map frontend field names to backend field names
    const backendPayload: Record<string, any> = {};
    if (updates.title !== undefined) backendPayload.title = updates.title;
    if (updates.description !== undefined) backendPayload.description = updates.description;
    if (updates.location !== undefined) backendPayload.location = updates.location;
    if (updates.start !== undefined) {
      backendPayload.start_time = updates.start instanceof Date ? updates.start.toISOString() : updates.start;
    }
    if (updates.end !== undefined) {
      backendPayload.end_time = updates.end instanceof Date ? updates.end.toISOString() : updates.end;
    }
    if (updates.allDay !== undefined) backendPayload.all_day = updates.allDay;
    if (updates.color !== undefined) backendPayload.color = updates.color;
    if (updates.calendarId !== undefined) backendPayload.calendar_name = updates.calendarId;
    if (updates.attendees !== undefined) backendPayload.attendees = updates.attendees;
    if (updates.reminders !== undefined) backendPayload.reminders = updates.reminders;
    if (updates.recurrence !== undefined) backendPayload.recurrence_rule = updates.recurrence;
    
    return this.request<CalendarEvent>(`/api/calendar/events/${encodeURIComponent(eventId)}`, {
      method: 'PUT',
      body: JSON.stringify(backendPayload),
    });
  }

  async deleteEvent(eventId: string): Promise<CalendarApiResponse<void>> {
    return this.request(`/api/calendar/events/${encodeURIComponent(eventId)}`, {
      method: 'DELETE',
    });
  }

  async rsvpToEvent(
    eventId: string,
    response: 'accepted' | 'declined' | 'tentative'
  ): Promise<CalendarApiResponse<void>> {
    return this.request(`/api/calendar/events/${encodeURIComponent(eventId)}/rsvp`, {
      method: 'POST',
      body: JSON.stringify({ response }),
    });
  }

  async createEventFromICS(
    icsContent: string
  ): Promise<CalendarApiResponse<CalendarEvent>> {
    return this.request<CalendarEvent>('/api/calendar/events/import', {
      method: 'POST',
      body: JSON.stringify({ icsContent }),
    });
  }

  async getUpcomingEvents(limit: number = 10): Promise<CalendarApiResponse<CalendarEvent[]>> {
    return this.request<CalendarEvent[]>(`/api/calendar/events/upcoming?limit=${limit}`);
  }
}

const calendarApiService = new CalendarApiService();
export default calendarApiService;
